import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  CreditCard,
  MessageCircle,
  ShieldCheck,
  TriangleAlert,
  Unlink,
} from "lucide-react";

import { apiClient, type MessagingChannel } from "@/api/client";
import { useMessaging } from "@/hooks/useMessaging";
import {
  formatRetentionDays,
  getMessagingState,
  getSafeTelegramEntryURL,
  getSafeWhatsAppEntryURL,
  getSafeMessagingIdentityMetadata,
  WHATSAPP_REGION_AVAILABILITY,
} from "@/lib/messaging";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface WhatsAppAccessSectionProps {
  isActiveSubscriber: boolean;
}

const MAX_UNLINK_ATTEMPTS = 4;

function waitForRetry(delayMs: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, delayMs));
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "Not available";
  const timestamp = new Date(value);
  if (!Number.isFinite(timestamp.getTime())) return "Not available";
  return timestamp.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function WhatsAppAccessSection({ isActiveSubscriber }: WhatsAppAccessSectionProps) {
  const { messagingStatus, isLoading, isError, refreshMessaging } = useMessaging();
  const [channel, setChannel] = useState<MessagingChannel>("signal");
  const [hasSelectedChannel, setHasSelectedChannel] = useState(false);
  const [isConfirmingUnlink, setIsConfirmingUnlink] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isWaitingForUnlink, setIsWaitingForUnlink] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const unlinkTriggerRef = useRef<HTMLButtonElement>(null);
  const cancelUnlinkRef = useRef<HTMLButtonElement>(null);
  const unlinkDialogRef = useRef<HTMLDivElement>(null);
  const isUnlinkingRef = useRef(isUnlinking);

  useEffect(() => {
    isUnlinkingRef.current = isUnlinking;
  }, [isUnlinking]);

  useEffect(() => {
    if (!isConfirmingUnlink) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelUnlinkRef.current?.focus();

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || isUnlinkingRef.current) return;
      event.preventDefault();
      setIsConfirmingUnlink(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousBodyOverflow;
      (previouslyFocused ?? unlinkTriggerRef.current)?.focus();
    };
  }, [isConfirmingUnlink]);

  const availableChannels: MessagingChannel[] = [
    "signal",
    ...(messagingStatus?.telegram_entry_url ||
    (messagingStatus?.telegram?.state && messagingStatus.telegram.state !== "unlinked")
      ? ["telegram" as const]
      : []),
    ...(messagingStatus?.whatsapp_entry_url ||
    (messagingStatus?.whatsapp?.state && messagingStatus.whatsapp.state !== "unlinked")
      ? ["whatsapp" as const]
      : []),
  ];

  useEffect(() => {
    if (!messagingStatus || hasSelectedChannel) return;
    const linkedChannel = availableChannels.find(
      candidate => getMessagingState(messagingStatus, candidate) !== "unlinked",
    );
    if (linkedChannel) setChannel(linkedChannel);
    setHasSelectedChannel(true);
  }, [availableChannels, hasSelectedChannel, messagingStatus]);

  const identity = channel === "signal"
    ? messagingStatus?.signal ?? null
    : channel === "telegram"
      ? messagingStatus?.telegram ?? null
      : messagingStatus?.whatsapp ?? null;
  const connectionState = getMessagingState(messagingStatus, channel);
  const isSignal = channel === "signal";
  const isTelegram = channel === "telegram";
  const connectionName = isSignal ? "Signal" : isTelegram ? "Telegram" : "WhatsApp";
  const deliveryProviderName = isSignal ? "Signal" : isTelegram ? "Telegram" : "Twilio";
  const { countryCode, identityLabel } = getSafeMessagingIdentityMetadata(identity);
  const isUnlinkPending = connectionState === "unlinking";
  const isLinked = connectionState === "linked" || connectionState === "opted_out" || isUnlinkPending;
  const canUseMessaging = messagingStatus?.eligibility?.can_use_messaging ?? isActiveSubscriber;
  const entryURL = isSignal
    ? null
    : isTelegram
      ? getSafeTelegramEntryURL(messagingStatus?.telegram_entry_url)
      : getSafeWhatsAppEntryURL(messagingStatus?.whatsapp_entry_url);
  const telegramEntryURL = getSafeTelegramEntryURL(messagingStatus?.telegram_entry_url);
  const rawContentRetention = formatRetentionDays(
    messagingStatus?.retention?.raw_content_days,
    7,
  );
  const conversationRetention = formatRetentionDays(
    messagingStatus?.retention?.conversation_days,
    30,
  );

  const handleUnlink = async () => {
    setIsUnlinking(true);
    setIsWaitingForUnlink(false);
    setMutationError(null);

    try {
      let lastObservedState = connectionState;

      for (let attempt = 0; attempt < MAX_UNLINK_ATTEMPTS; attempt += 1) {
        const result = await apiClient.unlinkMessaging(channel);
        await refreshMessaging(result.status, { revalidate: false });
        lastObservedState = getMessagingState(result.status, channel);

        if (!result.pending && lastObservedState === "unlinked") {
          setIsConfirmingUnlink(false);
          return;
        }

        if (attempt === MAX_UNLINK_ATTEMPTS - 1) break;

        setIsWaitingForUnlink(true);
        await waitForRetry(result.retryAfterMs);

        const observed = await apiClient.getMessagingStatus();
        await refreshMessaging(observed, { revalidate: false });
        lastObservedState = getMessagingState(observed, channel);
        if (lastObservedState === "unlinked") {
          setIsConfirmingUnlink(false);
          return;
        }
      }

      setMutationError(lastObservedState === "unlinking"
        ? "Podwise is still finishing the unlink. No completed unlink has been confirmed yet. Please wait a moment and try again."
        : `Podwise did not confirm that this ${connectionName} identity was unlinked. Please try again.`);
    } catch {
      setMutationError(`We couldn't confirm that this ${connectionName} identity was unlinked. No completed unlink was reported; please try again.`);
    } finally {
      setIsWaitingForUnlink(false);
      setIsUnlinking(false);
    }
  };

  const keepFocusInUnlinkDialog = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      unlinkDialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    );
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <section className={cn(theme.cards.base, "p-6")}>
      <div className="flex items-start gap-4">
        <div className={cn(theme.colors.primaryOrange, "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl")}>
          <MessageCircle className="h-6 w-6 text-gray-900 dark:text-white" aria-hidden="true" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
            {connectionName}
          </p>
          <h2 className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
            Ask Podwise from {connectionName}
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
            {isSignal
              ? "Ask questions about podcasts you follow directly from Signal. Podwise keeps the research and account controls independent of the Signal transport."
              : isTelegram
                ? "Ask questions about podcasts you follow directly from Telegram. Podwise keeps research, account controls, and payment outside the Telegram transport."
              : <>Ask questions about podcasts you follow without opening the app. WhatsApp is included with every active Podwise plan. {WHATSAPP_REGION_AVAILABILITY}</>}
          </p>
        </div>
      </div>

      {availableChannels.length > 1 ? (
        <div
          className="mt-6 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Messaging services"
        >
          {availableChannels.map(candidate => {
            const label = candidate === "signal"
              ? "Signal"
              : candidate === "telegram"
                ? "Telegram"
                : "WhatsApp";
            const selected = candidate === channel;
            const state = getMessagingState(messagingStatus, candidate);
            return (
              <button
                key={candidate}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  setChannel(candidate);
                  setHasSelectedChannel(true);
                  setMutationError(null);
                }}
                className={cn(
                  selected ? theme.buttons.primary : theme.buttons.secondary,
                  "rounded-lg px-4 py-2 text-sm font-semibold",
                )}
              >
                {label}{state === "linked" ? " · Connected" : ""}
              </button>
            );
          })}
        </div>
      ) : null}

      {mutationError && !isConfirmingUnlink ? (
        <div
          className={cn(theme.borders.error, theme.colors.errorBg, theme.text.error, "mt-6 rounded-xl p-4 text-sm")}
          role="alert"
        >
          {mutationError}
        </div>
      ) : null}

      <div className="mt-6">
        {isLoading ? (
          <div className="flex items-center gap-3 py-5 text-sm text-gray-500 dark:text-gray-400" role="status">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-orange dark:border-gray-600 dark:border-t-primary-orange-dark" aria-hidden="true" />
            Checking {connectionName} access…
          </div>
        ) : isError || !messagingStatus ? (
          <div className={cn(theme.borders.error, theme.colors.errorBg, "rounded-xl p-4")}>
            <p className={cn(theme.text.error, "text-sm")}>We couldn't load your messaging settings.</p>
            <button
              type="button"
              onClick={() => void refreshMessaging()}
              className={cn(theme.buttons.secondary, "mt-3 rounded-lg px-4 py-2 text-sm font-semibold")}
            >
              Try again
            </button>
          </div>
        ) : isLinked ? (
          <div className={cn(theme.borders.neubrutalism, "rounded-xl bg-white p-5 dark:bg-gray-800")}>
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  {connectionState === "linked" ? (
                    <CheckCircle2 className="h-5 w-5 text-primary-orange-dark dark:text-primary-orange" aria-hidden="true" />
                  ) : (
                    <TriangleAlert className="h-5 w-5 text-primary-orange-dark dark:text-primary-orange" aria-hidden="true" />
                  )}
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {isUnlinkPending
                      ? `${connectionName} unlink is finishing`
                      : connectionState === "linked"
                      ? canUseMessaging
                        ? `${connectionName} is connected`
                        : `${connectionName} is connected but paused`
                      : `${connectionName} messages are opted out`}
                  </p>
                </div>
                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">{connectionName} identity</dt>
                    <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                      {identityLabel ?? `Connected ${connectionName} account`}
                    </dd>
                  </div>
                  {countryCode ? (
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Registration country</dt>
                      <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                        {countryCode}
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Connected</dt>
                    <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                      {formatTimestamp(identity?.linked_at)}
                    </dd>
                  </div>
                  {identity?.consent_at ? (
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Consent recorded</dt>
                      <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                        {formatTimestamp(identity.consent_at)}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </div>

              <div className="flex flex-wrap gap-3">
                {entryURL && !isUnlinkPending ? (
                  <a
                    href={entryURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className={cn(theme.buttons.primary, "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold")}
                  >
                    Open {connectionName}
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                ) : null}
                {isActiveSubscriber ? (
                  <Link
                    to="/billing"
                    className={cn(theme.buttons.secondary, "inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold")}
                  >
                    Manage billing
                  </Link>
                ) : null}
                <button
                  ref={unlinkTriggerRef}
                  type="button"
                  onClick={() => setIsConfirmingUnlink(true)}
                  className={cn(theme.buttons.destructive, "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold")}
                >
                  <Unlink className="h-4 w-4" aria-hidden="true" />
                  {isUnlinkPending ? "Retry unlink" : "Unlink"}
                </button>
              </div>
            </div>

            {isUnlinkPending ? (
              <p className="mt-5 text-sm leading-6 text-gray-600 dark:text-gray-300" role="status">
                Podwise is waiting for an in-flight send to finish before removing the connection. A message already accepted by {deliveryProviderName} may still arrive. You can retry safely.
              </p>
            ) : connectionState === "opted_out" ? (
              <p className="mt-5 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Send <strong>START</strong> to Podwise in {connectionName} to opt back in, or unlink this account here.
              </p>
            ) : !canUseMessaging ? (
              <p className="mt-5 text-sm leading-6 text-gray-600 dark:text-gray-300">
                Your {connectionName} connection is saved, but answering is paused until your Podwise plan is active.{" "}
                <Link to="/paywall" className="font-semibold underline underline-offset-2">
                  Choose a plan
                </Link>
              </p>
            ) : null}
          </div>
        ) : (
          <div className={cn(theme.colors.warmYellowBg, theme.borders.neubrutalism, "rounded-xl p-5")}>
            <p className="font-semibold text-gray-900 dark:text-white">Connect a messaging account</p>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Start a private chat with Podwise in Signal or Telegram, then follow the account-link page in the reply. You will review the consent and retention terms before linking. Signup and payment stay on the Podwise website.
            </p>
            <p className="mt-2 text-sm font-medium leading-6 text-gray-700 dark:text-gray-200">
              Setup begins in the conversation. Never send passwords, sign-in codes, or payment-card details in chat.
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              {entryURL ? (
                <a
                  href={entryURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className={cn(theme.buttons.primary, "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold")}
                >
                  Start in {connectionName}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : isActiveSubscriber ? (
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Message the Podwise Signal number to begin setup.
                </p>
              ) : null}
              {telegramEntryURL && telegramEntryURL !== entryURL ? (
                <a
                  href={telegramEntryURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className={cn(theme.buttons.primary, "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold")}
                >
                  Start in Telegram
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : null}
              {!isActiveSubscriber ? (
                <Link
                  to="/paywall"
                  className={cn(theme.buttons.secondary, "inline-flex items-center justify-center rounded-xl px-5 py-3 font-semibold")}
                >
                  Choose a plan
                </Link>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className="mt-7 grid gap-4 border-t-2 border-gray-300 pt-6 dark:border-gray-600 md:grid-cols-3">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
            Only podcasts followed by your Podwise account are available.
          </p>
        </div>
        <div className="flex gap-3">
          <CreditCard className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
            {isSignal || isTelegram
              ? `Research requests use the same Podwise chat billing as the API; ${connectionName} transport itself is not charged again by Podwise.`
              : "Answer requests and messaging transport count toward your existing chat usage; there is no separate add-on."}
          </p>
        </div>
        <div className="flex gap-3">
          <Clock3 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
            {isSignal || isTelegram
              ? `Message contents and protected routing data are scheduled for redaction after ${rawContentRetention}; body-free delivery, billing, consent, and security records may remain for up to 1 year. Encrypted follow-up context is retained only for the active research scope and expires after the configured idle window.`
              : `Message contents and protected routing data are scheduled for redaction after ${rawContentRetention}; body-free delivery, billing, consent, and security records may remain for up to 1 year. Encrypted conversation memory may retain up to three recent question-and-answer excerpts for ${conversationRetention}.`}
          </p>
        </div>
      </div>

      <p className="mt-5 text-xs leading-5 text-gray-500 dark:text-gray-400">
        {isSignal
          ? "Signal transports the conversation; Podwise independently handles account linking, research, and billing. Send STOP at any time to opt out."
          : isTelegram
            ? "Telegram transports the conversation; Podwise independently handles account linking, research, and billing on the Podwise website. Send STOP at any time to opt out."
          : "Twilio and Meta process WhatsApp messages to deliver this service and may retain their own operational records. Send STOP at any time to opt out."}{" "}
        <Link to="/privacy" className="font-semibold underline underline-offset-2">
          Privacy Policy
        </Link>
        .
      </p>

      {isConfirmingUnlink ? createPortal(
        <div className="fixed inset-0 z-70 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
          <div
            ref={unlinkDialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="messaging-unlink-title"
            aria-describedby="messaging-unlink-description"
            onKeyDown={keepFocusInUnlinkDialog}
            className={cn(theme.cards.base, "my-auto w-full max-w-md p-6")}
          >
            <h2 id="messaging-unlink-title" className="text-xl font-bold text-gray-900 dark:text-white">
              {isUnlinkPending
                ? `Finish unlinking ${connectionName}?`
                : `Unlink ${connectionName}?`}
            </h2>
            <p id="messaging-unlink-description" className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {isUnlinkPending
                ? `Podwise is waiting for an in-flight send to finish before removing this connection. A message already accepted by ${deliveryProviderName} may still arrive. You can retry safely if the unlink does not finish here.`
                : `Podwise will stop answering through this ${connectionName} identity and cancel pending replies. A message already accepted by ${deliveryProviderName} may still arrive. You can connect again later from a new setup link.`}
            </p>
            {mutationError ? (
              <div
                className={cn(theme.borders.error, theme.colors.errorBg, theme.text.error, "mt-4 rounded-xl p-3 text-sm")}
                role="alert"
              >
                {mutationError}
              </div>
            ) : null}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                ref={cancelUnlinkRef}
                type="button"
                onClick={() => setIsConfirmingUnlink(false)}
                disabled={isUnlinking}
                className={cn(theme.buttons.secondary, "rounded-xl px-5 py-3 font-semibold")}
              >
                {isUnlinkPending ? "Close" : "Keep connected"}
              </button>
              <button
                type="button"
                onClick={() => void handleUnlink()}
                disabled={isUnlinking}
                className={cn(theme.buttons.destructive, "rounded-xl px-5 py-3 font-semibold", isUnlinking && "cursor-wait opacity-60")}
              >
                {isUnlinking
                  ? isWaitingForUnlink
                    ? "Finishing unlink…"
                    : "Unlinking…"
                  : isUnlinkPending
                    ? "Retry unlink"
                    : "Unlink account"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </section>
  );
}
